import { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Eye,
  RefreshCw,
  UserRound,
} from "lucide-react";
import { BackButton } from "../../../components/button/back-button";
import { LoadingButton } from "../../../components/button/loading-button";
import { ViewConversationModal } from "../../../components/view-conversation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import { useTenantFeatures } from "../../../contexts/tenant-features";
import { useAuth } from "../../../hooks/use-auth";
import api from "../../../services/api";
import type { Conversation } from "../../../types/conversation";
import { getApiErrorMessage } from "../../../utils";
import { getBrowserTimezone } from "../../../utils/report-period";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../permissions/access-control";

type FunnelKanbanColumnKey =
  | "aiAttendance"
  | "transferredToHuman"
  | "humanAttendance"
  | "scheduled"
  | "protocolled"
  | "closed";

type FunnelKanbanCard = {
  id: string;
  conversationId?: string;
  customerName: string;
  phone: string;
  attendant: string;
  status: string;
  createdAt?: string;
  scheduledAt?: string;
  closedAt?: string;
  transferReason?: string;
  handoffAt?: string;
  handoff_at?: string;
  awaitingConsentResponse?: boolean;
};

type FunnelKanbanColumn = {
  key: FunnelKanbanColumnKey;
  title: string;
  scope: "all_time" | "today";
  cards: FunnelKanbanCard[];
};

type FunnelKanbanResponse = {
  generatedAt: string;
  todayWindow: {
    start: string;
    end: string;
    tz: string;
  };
  columns: FunnelKanbanColumn[];
};

type ConversationLookup = {
  id: string;
  source: "active" | "closed";
};

const COLUMN_META: Record<
  FunnelKanbanColumnKey,
  {
    icon: typeof Clock3;
    accent: string;
    badge: string;
    title?: string;
  }
> = {
  aiAttendance: {
    icon: Clock3,
    accent: "border-t-blue-500",
    badge: "Todo histórico",
  },
  transferredToHuman: {
    icon: UserRound,
    accent: "border-t-amber-500",
    badge: "Todo histórico",
    title: "Aguardando Humano",
  },
  humanAttendance: {
    icon: UserRound,
    accent: "border-t-cyan-500",
    badge: "Todo histórico",
    title: "Atendimento Humano",
  },
  scheduled: {
    icon: CalendarCheck,
    accent: "border-t-violet-500",
    badge: "Agendados hoje",
  },
  protocolled: {
    icon: ClipboardList,
    accent: "border-t-amber-600",
    badge: "Protocolados hoje",
  },
  closed: {
    icon: CheckCircle2,
    accent: "border-t-slate-500",
    badge: "Encerrados hoje",
  },
};

const CLOSED_STATUS_META: Record<
  string,
  {
    label: string;
    className: string;
  }
> = {
  CLOSED_BY_HUMAN_ATTENDANCE: {
    label: "Encerrada por humano",
    className: "bg-sky-50 text-sky-700",
  },
  CLOSED_BY_TIMEOUT: {
    label: "Encerrada por inatividade",
    className: "bg-slate-100 text-slate-700",
  },
  CLOSED_BY_AI: {
    label: "Resolvida pela IA",
    className: "bg-emerald-50 text-emerald-700",
  },
};

function formatDateTime(value?: string) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatWaitingTime(value: string | undefined, now: number) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Math.max(0, now - date.getTime());
  const totalMinutes = Math.floor(diffMs / 60000);

  if (totalMinutes < 60) {
    return `Aguardando ${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) {
    return `Aguardando ${totalHours}h`;
  }

  return `Aguardando ${Math.floor(totalHours / 24)}d`;
}

function formatTransferReason(value: string) {
  const normalized = value.replace(/_+/g, " ").replace(/\s+/g, " ").trim();

  return normalized.replace(/^./, (char) => char.toUpperCase());
}

function getConversationLookup(
  card: FunnelKanbanCard,
  columnKey: FunnelKanbanColumnKey,
): ConversationLookup | null {
  if (
    columnKey === "closed" ||
    columnKey === "scheduled" ||
    columnKey === "protocolled"
  ) {
    return { id: card.id, source: "closed" };
  }

  if (!card.conversationId) return null;

  return { id: card.conversationId, source: "active" };
}

function RetryBox({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <p>{message}</p>
      <button type="button" className="btn-link-danger mt-2" onClick={onRetry}>
        Tentar novamente
      </button>
    </div>
  );
}

function KanbanCard({
  card,
  columnKey,
  now,
  openingConversationKey,
  onViewConversation,
}: {
  card: FunnelKanbanCard;
  columnKey: FunnelKanbanColumnKey;
  now: number;
  openingConversationKey: string | null;
  onViewConversation: (lookup: ConversationLookup) => void;
}) {
  const timestamp =
    columnKey === "scheduled"
      ? formatDateTime(card.scheduledAt)
      : formatDateTime(card.closedAt ?? card.createdAt);
  const conversationLookup = getConversationLookup(card, columnKey);
  const conversationKey = conversationLookup
    ? `${conversationLookup.source}:${conversationLookup.id}`
    : null;
  const waitingTime =
    columnKey === "transferredToHuman"
      ? formatWaitingTime(card.handoffAt ?? card.handoff_at, now)
      : "";
  const transferReason = card.transferReason
    ? formatTransferReason(card.transferReason)
    : "";

  return (
    <article className="rounded-md border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900">
            {card.customerName}
          </h3>
          <p className="mt-1 truncate text-xs text-slate-500">{card.phone}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {timestamp && (
            <span className="rounded bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
              {timestamp}
            </span>
          )}
          {conversationLookup && (
            <LoadingButton
              type="button"
              onClick={() => onViewConversation(conversationLookup)}
              className="btn-link-primary cursor-pointer"
              isLoading={openingConversationKey === conversationKey}
              loadingLabel="Carregando"
              title="Visualizar conversa"
              aria-label={`Visualizar conversa de ${card.customerName}`}
            >
              <Eye className="w-4 h-4" />
            </LoadingButton>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
        <UserRound size={14} className="shrink-0 text-slate-400" />
        <span className="min-w-0 truncate">{card.attendant}</span>
      </div>

      {card.awaitingConsentResponse && (
        <span className="mt-3 inline-flex w-fit rounded-full border border-amber-300/50 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
          Aguardando resposta
        </span>
      )}

      {columnKey === "transferredToHuman" && transferReason && (
        <p
          className="mt-3 max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded bg-amber-50 px-2 py-2 text-xs text-amber-800"
          title={transferReason}
        >
          {transferReason}
        </p>
      )}

      {waitingTime && (
        <span className="mt-3 inline-flex rounded bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700">
          {waitingTime}
        </span>
      )}

      {columnKey === "closed" && CLOSED_STATUS_META[card.status] && (
        <span
          className={`mt-3 inline-flex rounded px-2 py-1 text-[11px] font-medium ${CLOSED_STATUS_META[card.status].className}`}
        >
          {CLOSED_STATUS_META[card.status].label}
        </span>
      )}
    </article>
  );
}

function KanbanColumn({
  column,
  now,
  openingConversationKey,
  onViewConversation,
}: {
  column: FunnelKanbanColumn;
  now: number;
  openingConversationKey: string | null;
  onViewConversation: (lookup: ConversationLookup) => void;
}) {
  const meta = COLUMN_META[column.key];
  const Icon = meta.icon;
  const title = meta.title ?? column.title;

  return (
    <section
      className={`flex min-h-0 min-w-[260px] flex-1 flex-col rounded-md border border-slate-200 border-t-4 bg-slate-50 ${meta.accent}`}
    >
      <header className="border-b border-slate-200 px-3 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Icon size={18} className="shrink-0 text-slate-600" />
            <h2 className="truncate text-sm font-semibold text-slate-900">
              {title}
            </h2>
          </div>
          <span className="shrink-0 rounded bg-white px-2 py-1 text-xs font-semibold text-slate-700">
            {column.cards.length}
          </span>
        </div>
        <span className="mt-2 inline-flex rounded bg-white px-2 py-1 text-[11px] font-medium uppercase text-slate-500">
          {meta.badge}
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        {column.cards.length ? (
          column.cards.map((card) => (
            <KanbanCard
              key={card.id}
              card={card}
              columnKey={column.key}
              now={now}
              openingConversationKey={openingConversationKey}
              onViewConversation={onViewConversation}
            />
          ))
        ) : (
          <div className="flex flex-1 items-center justify-center rounded border border-dashed border-slate-200 px-3 py-8 text-center text-sm text-slate-500">
            Nenhuma conversa
          </div>
        )}
      </div>
    </section>
  );
}

export default function DashboardFunil() {
  const { user } = useAuth();
  const { protocolsEnabled, schedulingEnabled } = useTenantFeatures();
  const { error } = useToast();
  const canAccess = AccessControl.canView(
    APP_RESOURCES.PANEL_DASHBOARD_FUNNEL,
    user?.role,
  );
  const timezone = useMemo(() => getBrowserTimezone(), []);

  const [kanbanData, setKanbanData] = useState<FunnelKanbanResponse | null>(
    null,
  );
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [openingConversationKey, setOpeningConversationKey] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!canAccess) {
      setKanbanData(null);
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;

    async function fetchFunnelKanban() {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await api.get<FunnelKanbanResponse>(
          "/report/funnel-kanban",
          {
            params: { tz: timezone },
          },
        );

        if (!cancelled) {
          setKanbanData(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          const message = getApiErrorMessage(err);
          setLoadError(message);
          error(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchFunnelKanban();

    return () => {
      cancelled = true;
    };
  }, [canAccess, error, refreshToken, timezone]);

  function onRetry() {
    setRefreshToken((value) => value + 1);
  }

  function onRefresh() {
    setRefreshToken((value) => value + 1);
  }

  async function openConversation(lookup: ConversationLookup) {
    if (openingConversationKey) return;

    const conversationKey = `${lookup.source}:${lookup.id}`;

    try {
      setOpeningConversationKey(conversationKey);
      const endpoint =
        lookup.source === "closed"
          ? `/conversation/get-closed-by-id/${lookup.id}`
          : `/conversation/get-by-id/${lookup.id}`;
      const response = await api.get<Conversation>(endpoint);
      setSelectedConversation(response.data);
      setModalOpen(true);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setOpeningConversationKey(null);
    }
  }

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">Esteira de Atendimento</h1>
        <p className="app-page-subtitle">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  const columns = (kanbanData?.columns ?? []).filter(
    (column) =>
      (column.key !== "scheduled" || schedulingEnabled) &&
      (column.key !== "protocolled" || protocolsEnabled),
  );

  return (
    <div className="app-page mx-auto max-w-[1600px]">
      <BackButton />

      <div className="mb-5 mt-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="app-page-title">Esteira de Atendimento</h1>
          <p className="app-page-subtitle">
            Contatos organizados por etapa da conversa no sistema.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {kanbanData && (
            <div className="text-sm text-slate-500">
              Última carga: {formatDateTime(kanbanData.generatedAt)}
            </div>
          )}
          <button
            type="button"
            className="btn btn-secondary inline-flex items-center gap-2"
            onClick={onRefresh}
            disabled={isLoading}
            title="Atualizar esteira"
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </div>

      {loadError && <RetryBox message={loadError} onRetry={onRetry} />}

      {isLoading && !kanbanData ? (
        <div className="rounded border border-slate-200 bg-white p-6 text-sm text-slate-600">
          Carregando funil...
        </div>
      ) : (
        <div className="overflow-x-auto pb-3">
          <div className="flex h-[calc(100vh-260px)] min-h-[520px] gap-4">
            {columns.map((column) => (
              <KanbanColumn
                key={column.key}
                column={column}
                now={now}
                openingConversationKey={openingConversationKey}
                onViewConversation={openConversation}
              />
            ))}
          </div>
        </div>
      )}

      <ViewConversationModal
        isOpen={modalOpen}
        conversation={selectedConversation}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
